<script>
import { mapState, mapGetters, mapActions } from "vuex";

import InfoTooltip from "@/components/InfoTooltip";
import { formatHashrate, getBreakpoint } from "@/utils";
import WorkerList from "@/components/WorkerList";
import AutoRefreshToggle from "@/components/AutoRefreshToggle";
import NavigationWrapper from "@/components/NavigationWrapper";
import HashrateChart from "@/components/HashrateChart";
import AlertEvents from "@/components/AlertEvents";
import AlertHeatmap from "@/components/AlertHeatmap";
import FullScreenModal from "@/pages/FullScreenModal";

export default {
  // Included components
  components: {
    NavigationWrapper,
    InfoTooltip,
    HashrateChart,
    AlertEvents,
    AlertHeatmap,
    WorkerList,
    AutoRefreshToggle,
    FullScreenModal,
  },

  computed: {
    // Formatted hashrate data
    hashrate() {
      return this.teamShares.map((share) => [
        share[0],
        share[1] / 300, // 5 minutes
      ]);
    },

    // Mapped vuex state
    ...mapGetters(["activeTeam", "can"]),
    ...mapState(["teamStatistics", "teamShares", "teams", "teamContracts"]),
  },
  // Before the page is mounted, the user will be checked for the billing.manage permission
  // If they have it, it will then check if their most recent contract has been completed
  // If not completed, user will be rerouted to proper page so they can complete it
  // They will be unable to access the overview until they resolve the incomplete contract
  async beforeMount() {
    // Update Contracts in event of change
    await this.getTeamContracts();
    if (this.teamContracts.length > 0) {
      let most_recent_contract = this.teamContracts[0];
      if (this.can.billing.manage) {
        if (!most_recent_contract.payment_method_ingested) {
          await this.$router.push("/ingest-payment-method");
        } else if (!most_recent_contract.onboarding_invoice_created) {
          await this.$router.push("/confirm-charge");
        } else {
          return;
        }
      }
    } else {
      return;
    }
  },

  // When the component is created, fetch the team's statistics,
  // hashrate data, and alerts
  async created() {
    await this.getTeamShares({});
    this.updateStatistics();
  },

  methods: {
    // Update the hashrate chart data (every 5 minutes if autorefresh is on)
    async updateHashrateChartData() {
      console.log("update 5 minutes triggered");
      await this.getTeamShares({});
    },

    // Update the team statistics (every 30 seconds if autorefresh is on)
    updateStatistics() {
      this.getTeamStatistics({});
    },

    // Helper methods and mapped actions
    formatHashrate,
    getBreakpoint,
    ...mapActions([
      "getTeamStatistics",
      "getTeamShares",
      "getAlerts",
      "getTeamContracts",
    ]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item active>Overview</b-breadcrumb-item>
    </template>
    <b-row class="mb-2">
      <b-col>
        <h1>Overview</h1>
        <AutoRefreshToggle
          v-if="getBreakpoint().xs"
          text="Auto refresh overview"
          @update-5m="updateHashrateChartData()"
          @update-30s="updateStatistics()"
        />
      </b-col>
      <b-col
        cols="6"
        md="auto"
        :align-self="getBreakpoint().xs ? '' : 'center'"
        class="text-right"
      >
        <AutoRefreshToggle
          v-if="!getBreakpoint().xs"
          text="Auto refresh overview"
          @update-5m="updateHashrateChartData()"
          @update-30s="updateStatistics()"
        />
        <b-button
          :class="getBreakpoint().xs ? 'mt-2' : 'ml-3'"
          variant="primary"
          v-b-modal.fullscreen-modal
          v-if="Object.values(this.teams).length > 1"
        >
          Full Screen
        </b-button>
        <FullScreenModal modal-id="fullscreen-modal" />
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-card class="mb-3">
          <b-row>
            <b-col cols="6" :md="can.alerts.view ? 3 : 6" class="mb-2 md-sm-0">
              <div class="Worker-stat">
                <div class="Worker-stat-title">
                  <b>1d Hashrate</b>
                  &nbsp;
                  <InfoTooltip text="24 hr average hashrate" />
                </div>
                <div class="Worker-stat-value">
                  {{ formatHashrate(teamStatistics.hashrate_1d) }}
                </div>
              </div>
            </b-col>
            <b-col cols="6" :md="can.alerts.view ? 3 : 6" class="mb-2 mb-sm-0">
              <div class="Worker-stat">
                <div class="Worker-stat-title">
                  <b>30m Hashrate</b>
                  &nbsp;
                  <InfoTooltip text="30 minute average hashrate" />
                </div>
                <div class="Worker-stat-value">
                  {{ formatHashrate(teamStatistics.hashrate_30m) }}
                </div>
              </div>
            </b-col>
            <b-col cols="6" :md="can.alerts.view ? 3 : 6" class="mb-2 mb-sm-0">
              <div class="Worker-stat">
                <b>Active Workers</b>
                <div class="Worker-stat-value">
                  {{ teamStatistics.active_count }} / {{ teamStatistics.count }}
                </div>
              </div>
            </b-col>
            <b-col v-if="can.alerts.view" cols="6" md="3" class="mb-2 mb-sm-0">
              <div class="Worker-stat">
                <b>Alert Status</b>
                <div class="Worker-stat-value">
                  <span v-if="teamStatistics.triggering_alerts > 0">
                    <b-badge variant="danger">ALERT</b-badge>
                    &times;
                    {{ teamStatistics.triggering_alerts }}
                  </span>
                  <b-badge v-else variant="success">OK</b-badge>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-card no-body class="mb-3">
          <b-card-body class="p-2 p-md-3">
            <HashrateChart
              :axis-titles="false"
              :data="hashrate"
              :tick-amount="5"
              :canExport="true"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    <WorkerList :inactive="true" v-if="can.workers.view" class="mb-3" />
    <div v-if="can.alerts.view">
      <h3 class="mb-3">Alert Events</h3>
      <AlertHeatmap class="mb-3"></AlertHeatmap>
      <AlertEvents></AlertEvents>
    </div>
  </NavigationWrapper>
</template>

<style scoped>
.Worker-stat {
  text-align: center;
}

.Worker-stat-title {
  display: inline-flex;
  align-items: center;
}

.Worker-stat-value {
  font-size: 1.4rem;
}

.active .text-muted {
  color: white !important;
}
</style>
